import { createPublicClient, fallback, http } from "viem";
import { polygon } from "viem/chains";

const configuredPolygonRpc = import.meta.env.VITE_POLYGON_RPC_URL || "https://polygon.drpc.org";

const disabledPolygonRpcHosts = new Set([
  "polygon.api.onfinality.io",
  "polygon-mainnet.public.blastapi.io",
  "polygon-rpc.com",
  // These public endpoints do not reliably accept requests from browser
  // origins. BlockPI rejects CORS preflights and Llama is frequently
  // unreachable through hosted/browser network tunnels.
  "polygon.blockpi.network",
  "polygon.llamarpc.com",
]);

const nonArchivePolygonRpcHosts = new Set([
  "polygon-bor-rpc.publicnode.com",
  // dRPC works for ordinary reads but its anonymous Polygon endpoint rejects
  // otherwise valid eth_getLogs ranges with HTTP 400 responses.
  "polygon.drpc.org",
]);

const isUsablePolygonRpc = (url: string) => {
  try {
    return !disabledPolygonRpcHosts.has(new URL(url).hostname.toLowerCase());
  } catch {
    return false;
  }
};

const polygonRpcCandidates = Array.from(new Set([
  configuredPolygonRpc,
  "https://polygon.drpc.org",
  "https://polygon-bor-rpc.publicnode.com",
  "https://1rpc.io/matic",
  "https://polygon.gateway.tenderly.co",
])).filter(isUsablePolygonRpc);

export const polygonRpcUrls = polygonRpcCandidates.slice(0, 5);
export const polygonArchiveRpcUrls = polygonRpcCandidates.filter((url) => (
  !nonArchivePolygonRpcHosts.has(new URL(url).hostname.toLowerCase())
)).slice(0, 5);

const createRotatedPolygonTransport = (urls: string[], preferredProvider: number) => {
  const startIndex = preferredProvider % urls.length;
  const orderedUrls = [
    ...urls.slice(startIndex),
    ...urls.slice(0, startIndex),
  ];
  return fallback(orderedUrls.map((url) => http(url)), { rank: false });
};

export const createPolygonTransport = (preferredProvider = 0) => (
  createRotatedPolygonTransport(polygonRpcUrls, preferredProvider)
);

export const createPolygonArchiveTransport = (preferredProvider = 0) => (
  createRotatedPolygonTransport(polygonArchiveRpcUrls, preferredProvider)
);

// Each client starts with a different provider, then rotates through the
// remaining providers if its preferred endpoint fails.
export const polygonRpcClients = polygonRpcUrls.map((_, index) => createPublicClient({
  chain: polygon,
  transport: createPolygonTransport(index),
}));

// Historical log clients exclude endpoints that require a paid archive token.
export const polygonArchiveRpcClients = polygonArchiveRpcUrls.map((_, index) => createPublicClient({
  chain: polygon,
  transport: createPolygonArchiveTransport(index),
}));
